import { useContext, useMemo } from 'react';
import { ChartKey } from '@library/components/charts/ChartKey';
import { DonutChart } from '@library/components/charts/DonutChart';
import { NoResults } from '@library/components/NoResults';
import { NumberPanel } from '@library/components/numberPanel/NumberPanel';
import { PageBody } from '@library/components/layout/PageBody';
import { ResolutionDetailsContext } from '@bo-hooks/resolutions/resolutionDetails';
import { ResolutionDetailsSummaryTable } from '@bo-components/resolutions/resolutionDetails';
import { useResolutionFailures } from '@bo-hooks/resolutions/resolutionDetails/useResolutionFailures';

export const ResolutionDetailsSummary = () => {
  const { resolutionDetails } = useContext(ResolutionDetailsContext);

  const {
    actualAffectedWagers,
    actualRefundCents,
    estimatedAffectedWagers,
    status
  } = resolutionDetails;

  const {
    currentPage,
    data: failuresData = [],
    handleCurrentPageChange,
    isFetching: isFailuresFetching,
    totalPages
  } = useResolutionFailures();

  const totalFailed = useMemo(() => {
    return estimatedAffectedWagers - actualAffectedWagers;
  }, [actualAffectedWagers, estimatedAffectedWagers]);

  const isResolutionDiscarded = status === 'discarded';

  if (isResolutionDiscarded) {
    return <NoResults title="This resolution has been discarded." label="" />;
  }

  return (
    <PageBody className="flex grow flex-col items-center justify-center gap-6">
      <div className="flex w-[1000px] flex-col items-start gap-6">
        <div className="flex h-[42px] flex-col items-start self-stretch">
          <p className="text-soClean-100 text-base font-semibold">
            Completion summary
          </p>
          <p className="text-soClean-180 text-sm font-medium">
            The refund process has successfully completed.
          </p>
        </div>
        <div className="flex items-start gap-4 self-stretch">
          <div className="flex w-[440px] flex-col items-start gap-4 self-stretch">
            <NumberPanel
              field="Refunded Amount"
              quantity={actualRefundCents}
              refund
            />
            <div className="bg-soFresh-110 border-soFresh-130 flex items-center justify-center gap-9 self-stretch rounded-md border px-4 py-6 pb-4">
              <DonutChart
                successCount={actualAffectedWagers}
                failureCount={totalFailed}
              >
                <h2 className="text-soClean-100 text-lg">
                  {estimatedAffectedWagers}
                </h2>
                <p className="text-soClean-180 text-sm">Total Refunds</p>
              </DonutChart>
              <div className="flex flex-col items-start gap-4">
                <ChartKey
                  colorClass="bg-status-success"
                  graphArea="successful"
                  quantity={actualAffectedWagers}
                />
                <ChartKey
                  colorClass="bg-status-failure"
                  graphArea="failed"
                  quantity={totalFailed}
                />
              </div>
            </div>
          </div>
          <ResolutionDetailsSummaryTable
            currentPage={currentPage}
            handleCurrentPageChange={handleCurrentPageChange}
            isFailuresFetching={isFailuresFetching}
            totalPages={totalPages}
            failuresData={failuresData}
          />
        </div>
      </div>
    </PageBody>
  );
};
